import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function MatchRegistrationModal({ match, session, onClose, onSuccess }) {
  const [registrationType, setRegistrationType] = useState('solo'); // solo, team, custom
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [customMembers, setCustomMembers] = useState(['', '', '', '']);
  const [bgmiUsername, setBgmiUsername] = useState('');
  const [customTeamName, setCustomTeamName] = useState(''); // NEW: Team Name state
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    if (match.mode !== 'solo') {
      fetchUserTeams();
    }
    fetchWalletBalance();
  }, [match.mode]);

  const fetchUserTeams = async () => {
    try {
      const response = await fetch(`${API_BASE}/teams/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_BASE}/wallet/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setWalletBalance(data.balance_bigint || 0);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  const handleRegister = async () => {
    // Validation
    if (match.mode === 'solo' && registrationType === 'solo') {
      // Solo registration
    } else if (registrationType === 'team' && !selectedTeamId) {
      toast.error('Please select a team');
      return;
    } else if (registrationType === 'custom') {
      const filledMembers = customMembers.filter(m => m.trim());
      const requiredCount = match.mode === 'duo' ? 2 : 4; // Duo=2, Squad=4

      if (!customTeamName.trim()) {
        toast.error('Please enter a Team Name');
        return;
      }

      if (filledMembers.length !== requiredCount) {
        toast.error(`Please enter exactly ${requiredCount} BGMI usernames`);
        return;
      }
    }

    // Check wallet balance
    if (walletBalance < match.entry_fee * 100) {
      toast.error('Insufficient wallet balance. Please recharge!');
      return;
    }

    setLoading(true);
    try {
      let requestBody = {};

      if (registrationType === 'solo') {
        requestBody = {
          bgm_username: bgmiUsername, // Include username for solo
        };
      } else if (registrationType === 'team') {
        requestBody = { teamId: selectedTeamId };
      } else if (registrationType === 'custom') {
        const members = customMembers.filter(m => m.trim());
        requestBody = {
          team_members: members,
          team_name: customTeamName, // Send team_name
          // For custom squad/duo, we don't strictly need bgm_username separate if likely implied 
          // that the user is one of the members, or we just rely on team_members list.
          // However, backend might expect bgm_username for the registering user. 
          // Let's assume the first member is the registering user or backend handles it.
          // Based on user request "3 username -> 4 username", they want to input ALL members.
        };
      }

      const response = await fetch(`${API_BASE}/matches/${match.id}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Registration failed');
      }

      const data = await response.json();

      // If payment order is returned, handle Razorpay
      if (data.paymentOrder) {
        const { order, razorpayKeyId } = data.paymentOrder;

        const options = {
          key: razorpayKeyId,
          amount: order.amount,
          currency: order.currency,
          name: 'ZeroPing',
          description: `${match.title} Entry Fee`,
          order_id: order.id,
          handler: async function (response) {
            toast.success('Payment successful! Registration complete 🎮');
            onSuccess();
          },
          prefill: {
            email: session.user.email,
          },
          theme: {
            color: '#00f0ff',
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        // Wallet deduction successful
        onSuccess();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to register for match');
    } finally {
      setLoading(false);
    }
  };

  const getMemberCount = () => {
    switch (match.mode) {
      case 'duo': return 2; // User + 1 -> Total 2 inputs
      case 'squad': return 4; // User + 3 -> Total 4 inputs
      default: return 1;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', paddingBottom: '80px' }}>
        <div className="modal-header">
          <h3 className="modal-title">Register for Match</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div>
          {/* Match Info */}
          <div style={{
            background: 'var(--accent-bg)',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ fontSize: '1.3rem', marginBottom: '10px' }}>{match.title}</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '10px',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)'
            }}>
              <div>🗺️ Map: {match.map}</div>
              <div>🎯 Mode: {match.mode.toUpperCase()}</div>
              <div>💰 Entry Fee: ₹{match.entry_fee}</div>
              <div>📅 {new Date(match.start_ts).toLocaleString()}</div>
            </div>
          </div>

          {/* Wallet Balance */}
          <div style={{
            background: walletBalance >= match.entry_fee * 100 ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 51, 102, 0.1)',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: `1px solid ${walletBalance >= match.entry_fee * 100 ? 'var(--success-color)' : 'var(--danger-color)'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Wallet Balance:</span>
              <span style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: walletBalance >= match.entry_fee * 100 ? 'var(--success-color)' : 'var(--danger-color)'
              }}>
                ₹{(walletBalance / 100).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Registration Type Selection */}
          {match.mode !== 'solo' && (
            <div className="form-group">
              <label className="form-label">Registration Type</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {teams.length > 0 && (
                  <button
                    className={`btn ${registrationType === 'team' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setRegistrationType('team')}
                    style={{ flex: 1 }}
                  >
                    Use Team
                  </button>
                )}
                <button
                  className={`btn ${registrationType === 'custom' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setRegistrationType('custom')}
                  style={{ flex: 1 }}
                >
                  Custom Members
                </button>
              </div>
            </div>
          )}

          {/* Team Selection */}
          {registrationType === 'team' && (
            <div className="form-group">
              <label className="form-label">Select Team</label>
              <select
                className="form-input"
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
              >
                <option value="">-- Select a team --</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.members.length} members)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Members Input - OR SOLO */}
          {(registrationType === 'custom' || registrationType === 'solo') && (
            <>
              {/* Show "Your User Name" ONLY for Solo */}
              {match.mode === 'solo' && (
                <div className="form-group">
                  <label className="form-label">Your BGMI Username</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Your BGMI IGN"
                    value={bgmiUsername}
                    onChange={(e) => setBgmiUsername(e.target.value)}
                  />
                </div>
              )}

              {/* Show Team Name and Member Inputs for Custom (Duo/Squad) */}
              {registrationType === 'custom' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Team Name</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter Team Name"
                      value={customTeamName}
                      onChange={(e) => setCustomTeamName(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Team Members BGMI Usernames</label>
                    {/* Render inputs for ALL members (2 for Duo, 4 for Squad) */}
                    {Array.from({ length: getMemberCount() }).map((_, index) => (
                      <input
                        key={index}
                        type="text"
                        className="form-input"
                        placeholder={`Member ${index + 1} BGMI Username`}
                        value={customMembers[index] || ''}
                        onChange={(e) => {
                          const newMembers = [...customMembers];
                          newMembers[index] = e.target.value;
                          setCustomMembers(newMembers);
                        }}
                        style={{ marginBottom: '10px' }}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Registration Button */}
          <button
            className="btn btn-success"
            onClick={handleRegister}
            disabled={loading || walletBalance < match.entry_fee * 100}
            style={{ width: '100%', marginTop: '10px' }}
          >
            {loading ? 'Registering...' : `Pay ₹${match.entry_fee} & Register`}
          </button>

          {walletBalance < match.entry_fee * 100 && (
            <p style={{
              textAlign: 'center',
              marginTop: '15px',
              color: 'var(--danger-color)',
              fontSize: '0.9rem'
            }}>
              ⚠️ Insufficient balance. Please recharge your wallet first.
            </p>
          )}
        </div>
      </div>
    </div >
  );
}

export default MatchRegistrationModal;
